import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { ulid } from 'ulid';
import { catchError, map, switchMap } from 'rxjs/operators';
import { cleanTypename } from '../../segments/helpers/handleData.helper';
import { BehaviorSubject, forkJoin, Observable, of, tap, throwError } from 'rxjs';
import { CurrentUserService } from '@core/services/currentUser.service';
import { PermissionType } from '@admin/modules/users/models/user.model';
import {
  ADD_WORKFLOW,
  GET_CAMPAIGNS_LIST,
  GET_COLLECTOR,
  GET_WORKFLOW,
  GET_WORKFLOW_PRIORITY_BY_TENANT,
  GET_WORKFLOWS,
  PAUSE_WORKFLOW,
  RESUME_WORKFLOW,
  SEARCH_CAMPAIGNS_COLLECTORS_LIST,
  UNPUBLISH_WORKFLOW,
  UPDATE_WORKFLOW,
  UPDATE_WORKFLOW_PRIORITY,
} from '../graphql';
import { CAMPAIGN_TYPE, CampaignModel, CampaignType } from '../models/campaign.model';
import { reverseHackSuperNodes } from '@shared/components/joint-js/helpers';
import {
  UPDATE_NODE_PARAMETERS,
  UPDATE_WORKFLOW_COLLECTOR_IDS,
} from '@modules/campaigns/graphql/campaigns.graphql';
import {
  DELETE_WORKFLOW,
  GET_WORKFLOW_TRIGGERS,
} from '@modules/campaigns/graphql/workflows.graphql';
import { SEARCH_CAMPAIGN_COLLECTORS_CRITERIA } from '@modules/campaigns/graphql/campaings-collectors.graphql';

@Injectable({
  providedIn: 'root',
})
export class CampaignService {
  isLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private route: ActivatedRoute,
    private apollo: Apollo,
    private currentUser: CurrentUserService,
  ) {}

  getCampaignList(): Observable<any[]> {
    return this.apollo
      .query({
        query: GET_WORKFLOWS,
        variables: {
          tenantId: this.route.snapshot.queryParams['tenant'],
        },
        fetchPolicy: 'no-cache', // optional but helps avoid caching issues
      })
      .pipe(map((res) => (res.data as any).workflows));
  }

  getCampaignsDataPills(): Observable<any[]> {
    return this.apollo
      .query({
        query: GET_CAMPAIGNS_LIST,
        variables: {
          criteria: {
            filters: {
              tenantId: this.route.snapshot.queryParams['tenant'],
            },
          },
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          const campaigns = (res.data as any)?.campaigns;
          return Array.isArray(campaigns) ? campaigns : [];
        }),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  updateNodesConfig(status: string, campaign: any): Observable<any> {
    campaign = cleanTypename(campaign);
    let { id, alias, name, purpose, silenced, includedSegmentIds, nodes, bucket, triggers } = campaign;
    silenced = false;
    nodes = this.addAndCheckNodes(nodes);

    nodes.forEach((n: any) => {
      delete n.childrenIds;
    });

    return this.apollo
      .mutate({
        mutation: UPDATE_NODE_PARAMETERS,
        variables: {
          input: {
            workflowId: id,
            nodes: nodes
              .map((n: any) => {
                //UPDATE CHILDRENS IDS WITH CASES IDS
                if (
                  n.parameters.name === 'paylinkNotificationStatus' ||
                  n.parameters.name === 'customerCaseSegments'
                ) {
                  n.childrenIds = [];
                  n.childrenIds.push(n.parameters.cases.true);
                  n.childrenIds.push(n.parameters.cases.false);
                }
                return n;
              })
              .map((n: any) => reverseHackSuperNodes(n)),
          },
          workflowInput: {
            id,
            alias,
            name,
            status,
            silenced,
            nodes: [],
            includedSegmentIds,
            bucket,
            triggers,
          },
        },
      })
      .pipe(map((res: any) => res));
  }

  getSortedCampaignList(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      forkJoin([this.getCampaignList(), this.getCampaignListPriority()])
        .pipe(
          map(([campaigns, priority]) => {
            // Obtener los IDs prioritarios en el orden deseado
            const idsPrioritarios = priority.workflowIds.map((item: any) => item.id);

            // Ordenar las campañas según los IDs prioritarios
            campaigns.sort((a: any, b: any) => {
              const indexA = idsPrioritarios.indexOf(a.id);
              const indexB = idsPrioritarios.indexOf(b.id);

              if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
              } else if (indexA !== -1) {
                return -1;
              } else if (indexB !== -1) {
                return 1;
              } else {
                return 0;
              }
            });

            return campaigns;
          }),
        )
        .subscribe({
          next: (sortedCampaigns: any) => resolve(sortedCampaigns),
          error: (error: any) => reject(error),
        });
    });
  }

  getCampaignListPriority(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_WORKFLOW_PRIORITY_BY_TENANT,
          variables: {
            tenantId: this.route.snapshot.queryParams['tenant'],
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res.errors[0].message);
          } else {
            resolve(res.data.workflowPriorityByTenant.workflowIds);
          }
        });
    });
  }

  updateCampaignPriority(priority: any): Promise<void> {
    delete priority['__typename'];
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .mutate({
          mutation: UPDATE_WORKFLOW_PRIORITY,
          variables: {
            input: {
              tenantId: this.route.snapshot.queryParams['tenant'],
              workflowIds: priority,
            },
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res.errors[0].message);
          } else {
            resolve(res.data.updateWorkflow);
          }
        });
    });
  }

  getCampaign(id: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_WORKFLOW,
          variables: {
            id,
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            resolve(res);
          } else {
            resolve(res.data.workflow);
          }
        });
    });
  }

  getCampaignsCollectorsList(): Observable<any[]> {
    const tenantId = this.route.snapshot.queryParams['tenant'];

    return this.apollo
      .query({
        query: SEARCH_CAMPAIGNS_COLLECTORS_LIST,
        variables: {
          criteria: {
            tenantId,
          },
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res: any) => res.data?.campaignCollectors ?? []),
        switchMap((campaignCollectors: any[]) => {
          if (!campaignCollectors.length) {
            return of([]);
          }
          const requests = campaignCollectors.map((collector) =>
            this.apollo
              .query({
                query: GET_COLLECTOR,
                variables: { id: collector.collectorId },
                fetchPolicy: 'no-cache',
              })
              .pipe(map((response: any) => response.data.collector)),
          );

          return forkJoin(requests).pipe(
            map((collectorDetails) =>
              campaignCollectors.map((collector, index) => ({
                ...collector,
                detail: collectorDetails[index],
              })),
            ),
          );
        }),
      );
  }

  createCampaign(input: any): Observable<any> {
    const { name, alias, purpose, type, includedSegmentIds, triggers, bucket, collectorIds } =
      input;
    const mutationInput: Record<string, any> = {
      id: ulid(),
      type,
      includedSegmentIds,
      tenantId: this.route.snapshot.queryParams['tenant'],
      name,
      purpose,
      triggers,
      bucket,
      collectorIds,
    };
    if (alias) {
      mutationInput['alias'] = alias;
    }
    return this.apollo
      .mutate({
        mutation: ADD_WORKFLOW,
        variables: { input: mutationInput },
      })
      .pipe(map((res: any) => res));
  }

  pauseCampaign(campaign: any): Observable<any> {
    return this.apollo
      .mutate({
        mutation: PAUSE_WORKFLOW,
        variables: {
          input: {
            id: campaign.id,
          },
        },
      })
      .pipe(map((res: any) => res));
  }

  unPublishCampaign(campaign: any): Observable<any> {
    return this.apollo
      .mutate({
        mutation: UNPUBLISH_WORKFLOW,
        variables: {
          input: {
            id: campaign.id,
          },
        },
      })
      .pipe(map((res: any) => res));
  }

  getTriggers(): Observable<any> {
    return this.apollo
      .query({
        query: GET_WORKFLOW_TRIGGERS,
        variables: {
          tenantId: this.route.snapshot.queryParams['tenant'],
        },
      })
      .pipe(map((res: any) => res));
  }

  resumeCampaign(campaign: any): Observable<any> {
    return this.apollo
      .mutate({
        mutation: RESUME_WORKFLOW,
        variables: {
          input: {
            id: campaign.id,
          },
        },
      })
      .pipe(map((res: any) => res));
  }

  deleteDraftCampaign(campaign: any): Observable<any> {
    return this.apollo
      .mutate({
        mutation: DELETE_WORKFLOW,
        variables: {
          id: campaign.id,
        },
      })
      .pipe(map((res: any) => res));
  }

  replaceNodeIds(nodes: any): Node[] {
    const idMapping: { [key: string]: string } = {};

    nodes.forEach((node: any) => {
      idMapping[node.nodeId] = ulid();
    });

    return nodes.map((node: any) => {
      const newNode = { ...node, nodeId: idMapping[node.nodeId] };

      if (newNode.childrenIds) {
        newNode.childrenIds = newNode.childrenIds.map((id: string) => idMapping[id]);
      }

      if (newNode.parameters && newNode.parameters.targetNodeId) {
        newNode.parameters.targetNodeId = idMapping[newNode.parameters.targetNodeId];
      }

      if (newNode.parameters && newNode.parameters.cases) {
        if (typeof newNode.parameters.cases.true === 'string') {
          newNode.parameters.cases.true = idMapping[newNode.parameters.cases.true];
        }
        if (typeof newNode.parameters.cases.false === 'string') {
          newNode.parameters.cases.false = idMapping[newNode.parameters.cases.false];
        }
      }

      return newNode;
    });
  }

  cloneCampaign(campaign: any, _name?: string): Observable<any> {
    campaign = cleanTypename(campaign);
    const {
      name,
      purpose,
      type,
      includedSegmentIds,
      nodes,
      collectorIds,
      triggers,
      includedSegmentsIds,
    } = campaign;

    return this.apollo
      .mutate({
        mutation: ADD_WORKFLOW,
        variables: {
          input: {
            id: ulid(),
            tenantId: this.route.snapshot.queryParams['tenant'],
            name: _name ? _name : name + ' (Copy)',
            type,
            purpose,
            collectorIds,
            triggers,
            includedSegmentsIds,
          },
        },
      })
      .pipe(
        switchMap((res: any) => {
          const newCampaign = res.data.createWorkflow;
          const newCampaignId = newCampaign.id;

          return this.apollo.mutate({
            mutation: UPDATE_WORKFLOW,
            variables: {
              input: {
                id: newCampaignId,
                name: newCampaign.name,
                purpose,
                status: 'draft',
                silenced: false,
                includedSegmentIds,
                nodes: this.replaceNodeIds(nodes),
              },
            },
          });
        }),
        catchError((error) => {
          return throwError(() => new Error(error.message));
        }),
      );
  }

  addAndCheckNodes(nodes: any) {
    nodes.forEach((node: any) => {
      if (node.parameters.original) {
        delete node.parameters.original;
      }

      if (node.parameters.parent) {
        delete node.parameters.parent;
      }

      if (node.parameters.nodeNumber) {
        delete node.parameters.nodeNumber;
      }

      if (node.parameters.childrens) {
        delete node.parameters.childrens;
      }

      if (node.parameters.waitDays == '') {
        node.parameters.waitDays = 0;
      }
    });

    return nodes;
  }

  updateInfoCampaign(campaign: CampaignModel): Observable<any> {
    campaign = cleanTypename(campaign);

    return this.apollo
      .mutate({
        mutation: UPDATE_WORKFLOW,
        variables: {
          input: {
            id: campaign.id,
            alias: campaign.alias,
            name: campaign.name,
            purpose: campaign.purpose,
            status: campaign.status,
            silenced: false,
            includedSegmentIds: campaign.includedSegmentIds,
            bucket: campaign.bucket,
            triggers: campaign.triggers,
            nodes: campaign.nodes,
          },
        },
      })
      .pipe(
        map((res: any) => {
          if (res.errors?.length) {
            throw new Error(res.errors);
          }
          return res.data.updateWorkflow;
        }),
      );
  }

  updateInfoPublishedCampaign(campaign: CampaignModel): Observable<any> {
    campaign = cleanTypename(campaign);

    return this.apollo
      .mutate({
        mutation: UPDATE_NODE_PARAMETERS,
        variables: {
          input: {
            workflowId: campaign.id,
            nodes: campaign.nodes,
          },
          workflowInput: {
            alias: campaign.alias,
            id: campaign.id,
            name: campaign.name,
            purpose: campaign.purpose,
            status: campaign.status,
            silenced: false,
            includedSegmentIds: campaign.includedSegmentIds,
            bucket: campaign.bucket,
            triggers: campaign.triggers,
            nodes: [],
          },
        },
      })
      .pipe(
        map((res: any) => {
          if (res.errors?.length) {
            throw new Error(res.errors);
          }
          return res.data.updateWorkflow;
        }),
      );
  }

  updateWorkflowCollectorsIds(collectorIds: string[], workflowId: string): Observable<any> {
    return this.apollo
      .mutate({
        mutation: UPDATE_WORKFLOW_COLLECTOR_IDS,
        variables: {
          input: {
            id: workflowId,
            collectorIds: collectorIds,
          },
        },
      })
      .pipe(
        map((res: any) => {
          if (res.errors?.length) {
            throw new Error(res.errors);
          }
          return res.data.updateWorkflowCollectorIds;
        }),
      );
  }

  updateCampaign(status: string, campaign: any): Observable<any> {
    campaign = cleanTypename(campaign);
    let { id, alias, name, purpose, silenced, includedSegmentIds, nodes, bucket, triggers } = campaign;
    silenced = false;
    nodes = this.addAndCheckNodes(nodes);
    return this.apollo
      .mutate({
        mutation: UPDATE_WORKFLOW,
        variables: {
          input: {
            id,
            alias,
            name,
            purpose,
            status,
            silenced,
            includedSegmentIds,
            bucket,
            triggers,
            nodes: nodes
              .map((n: any) => {
                // Update children's IDs with cases IDs
                if (
                  n.parameters.name === 'paylinkNotificationStatus' ||
                  n.parameters.name === 'customerCaseSegments' ||
                  n.parameters.name === 'experiment'
                ) {
                  n.childrenIds = [];
                  n.childrenIds.push(n.parameters.cases.true);
                  n.childrenIds.push(n.parameters.cases.false);
                }
                return n;
              })
              .map((n: any) => reverseHackSuperNodes(n)),
          },
        },
      })
      .pipe(map((res: any) => res));
  }

  getCampaignCollectors(campaignId: string) {
    return this.apollo
      .watchQuery({
        query: SEARCH_CAMPAIGNS_COLLECTORS_LIST,
        variables: {
          criteria: {
            tenantId: this.route.snapshot.queryParams['tenant'],
          },
        },
      })
      .valueChanges.pipe(
        map((result: any) =>
          result.data.campaignCollectors.filter(
            (collector: any) => collector.campaignId === campaignId,
          ),
        ),
      );
  }

  getCampaignCollectorsCriteria(campaignId: string) {
    return this.apollo
      .watchQuery({
        query: SEARCH_CAMPAIGN_COLLECTORS_CRITERIA,
        variables: {
          criteria: {
            tenantId: this.route.snapshot.queryParams['tenant'],
          },
        },
      })
      .valueChanges.pipe(
        map((result: any) =>
          result.data.campaignCollectors.filter(
            (collector: any) => collector.campaignId === campaignId,
          ),
        ),
      );
  }

  canWrite() {
    return this.currentUser.isPermissionAuthorized([PermissionType.WORKFLOW_WRITE]);
  }

  isFullAccess() {
    return this.currentUser.isPermissionAuthorized([PermissionType.FULL_ACCESS]);
  }

  /**
   * @description
   * If the campaign type is pill return true, special type for puntual campaigns now name pills
   * @param {string} type
   * @returns {boolean}
   * @memberof CampaignService
   * @example
   * isPillType('pill') // true
   * isPillType('drip') // false
   */
  isPillType(type: CampaignType): boolean {
    return type === CAMPAIGN_TYPE.PILL;
  }
}
